import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { User } from '../../models/User';
import { getUser } from '../../selectors/user';
import { ROUTES } from '../routes';
import useGetCompanyById from './useGetCompanyById';
import { useGetFeatureEnabledForCountry } from './useGetFeatureEnabledForCountry';

export const WHATSAPP_VERIFICATION_MANDATORY_DATE = '2025-07-03';

const routesToIgnore = [
  new RegExp(`^/${ROUTES.login}/?$`),
  new RegExp(`^/${ROUTES.logout}/?$`),
  new RegExp(`^/${ROUTES.signup}/?$`),
  new RegExp(`^/${ROUTES.forgotPassword}/?$`),
  new RegExp(`^/${ROUTES.settings}(/.*)?$`),
];

type ShouldShowWhatsappVerificationParams = {
  mandatoryDate: string | null;
  user: Pick<User, 'isWhatsappVerified'> | null;
  validPath?: boolean;
  isWhatsappEnabled: boolean;
  companyCreatedAt?: string | null;
};

export function shouldShowWhatsappVerification({
  mandatoryDate,
  user,
  validPath = true,
  isWhatsappEnabled,
  companyCreatedAt,
}: ShouldShowWhatsappVerificationParams) {
  return Boolean(
    mandatoryDate &&
      user &&
      validPath &&
      !user.isWhatsappVerified &&
      isWhatsappEnabled &&
      companyCreatedAt &&
      dayjs(dayjs(companyCreatedAt).format('YYYY-MM-DD')).isSameOrAfter(
        dayjs(mandatoryDate)
      )
  );
}

export default function useShouldShowWhatsappVerification() {
  const { company } = useGetCompanyById();
  const { isWhatsappEnabled } = useGetFeatureEnabledForCountry();

  const user: User | null = useSelector(getUser);
  const location = useLocation();
  const validPath = !routesToIgnore.some(regex =>
    regex.test(location.pathname)
  );
  const shouldShowWhatsappVerificationResult = shouldShowWhatsappVerification({
    mandatoryDate: WHATSAPP_VERIFICATION_MANDATORY_DATE,
    user,
    validPath,
    isWhatsappEnabled,
    companyCreatedAt: company?.createdAt,
  });

  return shouldShowWhatsappVerificationResult;
}
